/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Cell } from "react-table";
import { Spinner, Text } from "@nordcloud/gnui";
import { FlexContainer, Table } from "~/components";
import { PDServiceRemove } from "~/components/Applications/ApplicationDetails/PDServiceRemove";
import { PagerDutyLink } from "~/components/Utils/PagerDutyLink";
import { Application, ServiceTableEntry } from "~/models";
import { PDEscalationPolicy, PDService } from "~/models/pagerDuty";
import { getServicesTableData } from "~/tools/utils/pagerDuty";

type Props = {
  application: Application;
  services: PDService[];
  escalationPolicies: PDEscalationPolicy[];
  loading: boolean;
  canEdit: boolean;
  removeService: (serviceId: string) => Promise<void>;
};

function PDServicesTable({
  application,
  services,
  escalationPolicies,
  loading,
  canEdit,
  removeService,
}: Props) {
  const columns = React.useMemo(
    () => [
      {
        Header: "Service Name",
        accessor: "name",
        Cell: ({ row, value }: Cell<ServiceTableEntry>) => (
          <PagerDutyLink type="service" id={row.original.pagerdutyId}>
            {value}
          </PagerDutyLink>
        ),
      },
      {
        Header: "Integration",
        accessor: "integration",
      },
      {
        Header: "Current Escalation Policy",
        accessor: "epName",
        Cell: ({ row, value }: Cell<ServiceTableEntry>) => (
          <PagerDutyLink type="escalationPolicy" id={row.original.epId}>
            {value}
          </PagerDutyLink>
        ),
      },
      {
        Header: "Soft Launch Escalation Policy",
        accessor: "softLaunchEpName",
        Cell: ({ row, value }: Cell<ServiceTableEntry>) =>
          row.original.softLaunchEpId ? (
            <PagerDutyLink
              type="escalationPolicy"
              id={row.original.softLaunchEpId}
            >
              {value}
            </PagerDutyLink>
          ) : (
            value
          ),
      },
      {
        Header: "Target Escalation Policy",
        accessor: "targetEpName",
        Cell: ({ row, value }: Cell<ServiceTableEntry>) =>
          row.original.targetEpId ? (
            <PagerDutyLink type="escalationPolicy" id={row.original.targetEpId}>
              {value}
            </PagerDutyLink>
          ) : (
            value
          ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }: Cell<ServiceTableEntry>) => {
          return (
            canEdit && (
              <FlexContainer>
                <PDServiceRemove
                  removeCallback={removeService}
                  service={row?.original}
                />
              </FlexContainer>
            )
          );
        },
      },
    ],
    [canEdit, removeService]
  );

  if (!application.pdServices.length) {
    return <Text mt={3}>No PagerDuty services</Text>;
  }

  if (loading) {
    return (
      <div css={{ marginTop: "1rem" }}>
        <Spinner />
      </div>
    );
  }

  const tableData = getServicesTableData(
    application.pdServices,
    services,
    escalationPolicies
  );

  return (
    <div css={{ marginTop: "1rem" }}>
      <Table columns={columns} data={tableData} mb="0" />
    </div>
  );
}

export { PDServicesTable };
